import { ColumnMapping } from '../../types/master';
import { MatchKeySource, resolveFilterKeys, resolveSearchKey } from './matchKeys';

/**
 * パターン別設定タブのステップ間依存を解決する（#3180）。
 *
 * あるステップの「入力」（絞り込みキー・検索キーの item_id）が、
 * 別ステップの「出力」（column_mappings の格納先 item_id）になっている場合、
 * そのステップは別ステップに依存する。
 *
 * 依存元が自分より後ろにあると、照合時にまだ値が入っていないため機能しない。
 * これを警告として利用者に伝えるための情報を組み立てる。
 *
 * 判定はブロックには使わない（順序の最終判断はユーザーに委ねる）。
 */

/** 依存解決の入力。表示順に並べた1ステップ分。 */
export interface DependencyStep extends MatchKeySource {
  /** ステップの識別子（mapping_id） */
  mapping_id: string;
  /** そのステップが値を書き込む先 */
  column_mappings?: ColumnMapping[];
}

/** 依存元ステップの参照。番号は1始まり（画面の①②と一致させる）。 */
export interface DependencySource {
  /** 依存元の表示番号（1始まり） */
  order: number;
  /** 依存している項目のラベル（警告表示に使う） */
  itemLabel: string;
}

/** 1つの入力項目が、どのステップの出力に依存しているか */
export interface ItemDependency {
  /** 自分より前にある依存元の表示番号（正しい順序） */
  satisfiedOrders: number[];
  /** 自分より後ろにある依存元の表示番号（順序が逆＝警告） */
  violatedOrders: number[];
}

export interface StepDependency {
  /** 自分より前にある依存元（正しい順序。情報表示に使う） */
  satisfied: DependencySource[];
  /** 自分より後ろにある依存元（順序が逆。警告表示に使う） */
  violated: DependencySource[];
  /**
   * 入力項目（item_id）ごとの依存元（#3180）。
   * 絞り込み条件・照合キーの各行に「※①の取得項目を使用しています」と
   * 注記するために使う。依存の無い項目はキー自体を持たない。
   */
  byItemId: Map<string, ItemDependency>;
}

/**
 * 各ステップの依存関係を、表示順の配列と同じ並びで返す。
 *
 * @param steps 表示順（＝実行順）に並べたステップ配列
 */
export const resolveStepDependencies = (steps: DependencyStep[]): StepDependency[] => {
  // 「出力項目 item_id → その値を出力するステップの index 集合」を先に作る。
  // 同じ item_id を複数ステップが出力することは排他制御上まれだが、
  // 起こりうる（別マスタが同じ格納先を持つ）ため集合で持つ。
  const producersByItemId = new Map<string, number[]>();
  steps.forEach((step, index) => {
    for (const columnMapping of step.column_mappings ?? []) {
      if (!columnMapping.item_id) continue;
      const producers = producersByItemId.get(columnMapping.item_id) ?? [];
      producers.push(index);
      producersByItemId.set(columnMapping.item_id, producers);
    }
  });

  return steps.map((step, selfIndex) => {
    // このステップの入力＝絞り込みキーと検索キーが読む項目。
    const inputs = [
      ...resolveFilterKeys(step).map(key => ({
        itemId: key.item_id,
        itemLabel: key.item_label,
      })),
      ((): { itemId: string; itemLabel: string } => {
        const searchKey = resolveSearchKey(step);
        return { itemId: searchKey.item_id, itemLabel: searchKey.item_label };
      })(),
    ].filter(input => !!input.itemId);

    const satisfied = new Map<number, DependencySource>();
    const violated = new Map<number, DependencySource>();
    const byItemId = new Map<string, ItemDependency>();

    for (const input of inputs) {
      const producers = producersByItemId.get(input.itemId) ?? [];
      for (const producerIndex of producers) {
        // 自分自身の出力は依存とみなさない（自分の照合結果を自分の入力には使えない）。
        if (producerIndex === selfIndex) continue;

        const source: DependencySource = {
          order: producerIndex + 1,
          itemLabel: input.itemLabel,
        };
        // 同じステップに複数項目で依存する場合、情報表示では番号を1つにまとめたいので
        // Map のキーを index にして重複を潰す（警告側は最初に見つかった項目名を残す）。
        if (producerIndex < selfIndex) {
          if (!satisfied.has(producerIndex)) satisfied.set(producerIndex, source);
        } else {
          if (!violated.has(producerIndex)) violated.set(producerIndex, source);
        }

        // 項目ごとの依存元も記録する（絞り込み条件の各行に注記するため）。
        const itemDependency = byItemId.get(input.itemId) ?? {
          satisfiedOrders: [],
          violatedOrders: [],
        };
        const orders =
          producerIndex < selfIndex
            ? itemDependency.satisfiedOrders
            : itemDependency.violatedOrders;
        if (!orders.includes(source.order)) orders.push(source.order);
        byItemId.set(input.itemId, itemDependency);
      }
    }

    const byOrderAsc = (a: DependencySource, b: DependencySource): number => a.order - b.order;
    const ascending = (a: number, b: number): number => a - b;
    byItemId.forEach(itemDependency => {
      itemDependency.satisfiedOrders.sort(ascending);
      itemDependency.violatedOrders.sort(ascending);
    });

    return {
      satisfied: Array.from(satisfied.values()).sort(byOrderAsc),
      violated: Array.from(violated.values()).sort(byOrderAsc),
      byItemId,
    };
  });
};
